import { Schema, SchemaSettings } from '@orbit/data'
import { CardioExercise, StrengtheningExercise } from '@/jsonapi/PatientExercise'
import { ExerciseProgramSessionAttributes } from '@/jsonapi/ExerciseProgramSession'
import { findRemoteIdFromNewRecord, KC } from '@/http/utils'

import { DEFAULT_CARDIO_EXERCISES, DEFAULT_STRENGTHENING_EXERCISES } from '@/store/modules/exercise/constants'

enum ModelTypes {
  ExerciseProgram = 'program',
}

enum ModelTypeNames {
  ExerciseProgram = 'program',
}

export interface ExerciseProgramAttributes {
  createdByUsername: string
  createdTimestamp: number
  lastUpdatedByUsername: string
  lastUpdatedTimestamp: number
  site: string
  startDate: string
  endDate: string | null
  title: string
  description: string | null
  resourceId: string
  participants: string[]
  sessions: ExerciseProgramAttributes[]
  strengtheningExercises: StrengtheningExercise[] | null
  cardioExercises: CardioExercise[] | null
}

export interface ExerciseProgramSchema {
  id: string
  attributes: ExerciseProgramAttributes
}

const schemaDefinition: SchemaSettings = {
  models: {
    [ModelTypeNames.ExerciseProgram]: {
      attributes: {
        createdByUsername: { type: 'string' },
        createdTimestamp: { type: 'number' },
        lastUpdatedByUsername: { type: 'string' },
        lastUpdatedTimestamp: { type: 'number' },
        site: { type: 'string' },
        startDate: { type: 'string' },
        endDate: { type: 'string' },
        title: { type: 'string' },
        description: { type: 'string' },
        resourceId: { type: 'string' },
        participants: { type: 'array' },
        sessions: { type: 'array' },
        strengtheningExercises: { type: 'array' },
        cardioExercises: { type: 'array' },
      },
    },
  },
}

export async function getExerciseProgramQuery() {
  const schema = new Schema(schemaDefinition)
  const remote = await KC.getJsonApiRemoteResource(schema)
  return remote
    .query(q => {
      console.log('Before calling find record for exercise programs')
      return q.findRecords(ModelTypes.ExerciseProgram)
    })
    .catch(e => {
      console.log('fetch failed for exercise programs')
      console.log(e)
    })
}

export async function getExerciseProgramByExerciseProgramIdQuery({ programId }: { programId: string }) {
  const schema = new Schema(schemaDefinition)
  const remote = await KC.getJsonApiRemoteResource(schema)
  console.log('Before calling find record for exercise program', programId)
  return remote
    .query(q => q.findRecords(ModelTypes.ExerciseProgram).filter({ attribute: 'id', value: programId }))
    .catch(() => console.log('fetch failed in get exercise program', programId))
}

export type PostExerciseProgramPayload = {
  site: string
  title: string
  description: string
  startDate: string
  endDate: string
  participants: string[]
}

export async function postExerciseProgramQuery(payload: PostExerciseProgramPayload) {
  const schema = new Schema(schemaDefinition)
  const remote = await KC.getJsonApiRemoteResourceForNewRecord(schema)
  const result = await remote.push(q => {
    console.log('before calling push record for exercise program')
    return q.addRecord({
      id: '',
      type: ModelTypeNames.ExerciseProgram,
      attributes: {
        site: payload.site,
        startDate: payload.startDate,
        endDate: payload.endDate,
        title: payload.title,
        description: payload.description,
        participants: payload.participants,
        cardioExercises: DEFAULT_CARDIO_EXERCISES,
        strengtheningExercises: DEFAULT_STRENGTHENING_EXERCISES,
      },
    })
  })
  return findRemoteIdFromNewRecord(result)
}

export async function patchExerciseProgramQuery(payload) {
  const schema = new Schema(schemaDefinition)
  const remote = await KC.getJsonApiRemoteResource(schema)
  return remote.push(q => {
    console.log('Before calling update record for exercise program')
    return q.updateRecord({
      id: payload.id,
      type: ModelTypeNames.ExerciseProgram,
      attributes: payload.attributes,
    })
  })
}

export async function deleteExerciseProgramQuery(programId: string) {
  const schema = new Schema(schemaDefinition)
  const remote = await KC.getJsonApiRemoteResource(schema)
  return remote.push(q => {
    console.log('Before calling remove record for exercise program')
    return q.removeRecord({
      id: programId,
      type: ModelTypeNames.ExerciseProgram,
    })
  })
}
